import { initializeApp } from "firebase/app";
import videoSnapshot from "./generated/videos.json";

const firebaseConfig = {
  apiKey: "AIzaSyAQ8ajG6fz2nydqWuiHzvv6TlHIr0z9c6g",
  authDomain: "myvidstash.firebaseapp.com",
  databaseURL: "https://myvidstash-default-rtdb.firebaseio.com",
  projectId: "myvidstash",
  storageBucket: "myvidstash.appspot.com",
  messagingSenderId: "607708005049",
  appId: "1:607708005049:web:dc2c27e952c26312951168",
  measurementId: "G-H8M0C47Z35"
};

const app = initializeApp(firebaseConfig);

const CACHE_KEY = 'pw_videos_v3';
const STORAGE_API = `https://firebasestorage.googleapis.com/v0/b/${firebaseConfig.storageBucket}/o`;
const FIRESTORE_API = `https://firestore.googleapis.com/v1/projects/${firebaseConfig.projectId}/databases/(default)/documents`;

// Must match the URLs prefetched by the inline script in public/index.html.
const VIDEO_LIST_URL = `${STORAGE_API}?prefix=videos%2F&delimiter=%2F`;
const VIDEO_DOCS_URL = `${FIRESTORE_API}/videos?pageSize=300&mask.fieldPaths=likes&mask.fieldPaths=likeCount&mask.fieldPaths=createdAt&key=${firebaseConfig.apiKey}`;

// The Auth and Firestore SDKs are large and gate their first request on Auth start-up,
// so the public grid reads over REST and the SDKs load only when something needs them.
const loadAuth = () => import('firebase/auth').then((m) => ({ ...m, auth: m.getAuth(app) }));
const loadFirestore = () => import('firebase/firestore').then((m) => ({ ...m, db: m.getFirestore(app) }));

const mediaURL = (path) => `${STORAGE_API}/${encodeURIComponent(path)}?alt=media`;

const thumbnailPathFor = (videoName) => `thumbnails/${videoName.replace(/\.(mov|mp4)$/i, '.jpg')}`;

// Small WebP copies bundled with the site by scripts/build-snapshot.js; null for videos added since the last run.
const tileNames = new Set(videoSnapshot.filter((v) => v.tile).map((v) => v.name));
const tileURLFor = (videoName) =>
  tileNames.has(videoName) ? `/thumbs/${videoName.replace(/\.(mov|mp4)$/i, '')}.webp` : null;

const toVideo = (fileName, stats) => ({
  videoId: `videos/${fileName}`,
  title: fileName.split('.')[0],
  videoURL: mediaURL(`videos/${fileName}`),
  thumbnailURL: mediaURL(thumbnailPathFor(fileName)),
  tileURL: tileURLFor(fileName),
  ...stats,
});

const videoFromName = (fileName) => toVideo(fileName, { likes: 0, views: 0, created: 0 });

// Build-time copy of the list, shown until Firebase responds.
const snapshotVideos = () =>
  videoSnapshot.map((v) => toVideo(v.name, { likes: v.likes, views: v.views, created: v.created }));

// Older docs store likes as an array of uids, newer ones as a number or likeCount.
const likeCountOf = (data = {}) => {
  if (Array.isArray(data.likes)) return data.likes.length;
  if (typeof data.likes === 'number') return data.likes;
  return data.likeCount || 0;
};

const restLikeCount = (fields = {}) => {
  const { likes, likeCount } = fields;
  if (likes?.arrayValue) return (likes.arrayValue.values || []).length;
  if (likes?.integerValue !== undefined) return Number(likes.integerValue);
  if (likes?.doubleValue !== undefined) return Number(likes.doubleValue);
  return Number(likeCount?.integerValue || 0);
};

const readCachedVideos = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
    return Array.isArray(cached) && cached.length ? cached : null;
  } catch {
    return null;
  }
};

const writeCachedVideos = (videos) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(videos));
  } catch {
    // Storage full or blocked; the cache is only a speed-up.
  }
};

const getJSON = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${url.split('?')[0]}`);
  return res.json();
};

const takePrefetched = (key) => {
  const pending = window.__pwPrefetch?.[key];
  if (pending) delete window.__pwPrefetch[key];
  return pending;
};

const fetchAllPages = async (url, listKey, prefetched) => {
  let page = await (prefetched ? prefetched.catch(() => getJSON(url)) : getJSON(url));
  const items = [...(page[listKey] || [])];
  while (page.nextPageToken) {
    page = await getJSON(`${url}&pageToken=${encodeURIComponent(page.nextPageToken)}`);
    items.push(...(page[listKey] || []));
  }
  return items;
};

const fetchVideos = async () => {
  const [files, docs] = await Promise.all([
    fetchAllPages(VIDEO_LIST_URL, 'items', takePrefetched('list')),
    fetchAllPages(VIDEO_DOCS_URL, 'documents', takePrefetched('docs')),
  ]);
  const fieldsById = new Map(docs.map((d) => [d.name.split('/').pop(), d.fields || {}]));
  const previous = new Map((readCachedVideos() || snapshotVideos()).map((v) => [v.videoId, v]));

  return files
    .filter(({ name }) => name.length > 'videos/'.length)
    .map(({ name: fullPath }) => {
      const fileName = fullPath.slice('videos/'.length);
      const fields = fieldsById.get(fileName) || {};
      const cached = previous.get(fullPath);
      const createdAt = Date.parse(fields.createdAt?.timestampValue);
      return toVideo(fileName, {
        likes: restLikeCount(fields),
        views: cached?.views ?? 0,
        created: Number.isNaN(createdAt) ? cached?.created ?? 0 : createdAt,
      });
    });
};

// View counts live in Storage custom metadata, which needs one request per file.
const fetchStorageStats = async (videos) => {
  const results = await Promise.allSettled(
    videos.map((v) => getJSON(`${STORAGE_API}/${encodeURIComponent(v.videoId)}`))
  );
  const enriched = videos.map((v, i) => {
    if (results[i].status !== 'fulfilled') return v;
    const meta = results[i].value;
    return {
      ...v,
      views: parseInt(meta.metadata?.views || '0', 10),
      created: v.created || Date.parse(meta.timeCreated) || 0,
    };
  });
  writeCachedVideos(enriched);
  return enriched;
};

const incrementVideoViews = async (videoId) => {
  const { getStorage, ref, getMetadata, updateMetadata } = await import('firebase/storage');
  const videoRef = ref(getStorage(app), videoId);
  const metadata = await getMetadata(videoRef);
  const views = parseInt(metadata.customMetadata?.views || '0', 10) + 1;
  await updateMetadata(videoRef, { customMetadata: { views: views.toString() } });
  return views;
};

export {
  fetchStorageStats,
  fetchVideos,
  incrementVideoViews,
  likeCountOf,
  loadAuth,
  loadFirestore,
  mediaURL,
  readCachedVideos,
  snapshotVideos,
  tileURLFor,
  videoFromName,
};
